import type { NewsletterAnswer, NewsletterAnswerKind } from '~/utils/newsletterAnswer'
import { newsletterAnswerSilences, parseNewsletterAnswer } from '~/utils/newsletterAnswer'
import { LEGACY_NEWSLETTER_ANSWER_KEY, migratedNewsletterDismissalAt, readScratchStore } from '~/utils/scratchStore'

/**
 * The newsletter popup — the offer the scratcher replaced, shown again whenever
 * no scratch card is active. Its shared open state, the record of whether the
 * visitor has already answered it, and the flag that keeps its lazy component
 * (`NewsletterModal`, from the default layout) mounted once it has been shown.
 *
 * It opens two ways, and neither lives here:
 * - automatically, from `useScratchTrigger`: the exit-intent trap the scratcher
 *   uses, for a logged-out visitor who has not answered it, when no card is
 *   active — so the two can never compete for the same page load;
 * - on demand from the announcement strip (`useScratchFlowCore.requestManualOpen`)
 *   for anyone, at any time, when no card is active.
 *
 * Named apart from `useNewsletterModal`, which is the strip's opener for the
 * whole offer (scratcher or newsletter) and keeps that name for it.
 */

/**
 * Mirror of the stored answer for browsers that refuse localStorage — hardened
 * profiles, Safari's private mode. Module scope, so it survives client-side
 * navigation and the offer is made at most once per page load there. Treating a
 * storage failure as "already answered" instead would silently hide the offer
 * from that whole population.
 */
let inMemoryAnswer: NewsletterAnswer | null = null

export function useNewsletterPopup() {
  const open = useState<boolean>('newsletter-popup', () => false)
  /** Sticky once true, so the lazy modal stays mounted and can animate out. */
  const mounted = useState<boolean>('newsletter-popup-mounted', () => false)

  /**
   * The stored answer and this page load's own, merged: a sign-up in either
   * wins, otherwise the newer one. Storage can be readable yet refuse writes (a
   * full quota), and then an old stored answer must not hide a fresh one.
   */
  function readAnswer(): NewsletterAnswer | null {
    if (!import.meta.client)
      return null
    let stored: NewsletterAnswer | null = null
    try {
      stored = parseNewsletterAnswer(window.localStorage.getItem(LEGACY_NEWSLETTER_ANSWER_KEY))
    }
    catch {
      // Unreadable — this page load's own answer is all there is.
    }
    const own = inMemoryAnswer
    if (stored?.kind === 'subscribed')
      return stored
    if (own?.kind === 'subscribed')
      return own
    if (!own)
      return stored
    return stored && stored.at >= own.at ? stored : own
  }

  /**
   * True while the automatic trap must stay quiet. Opening from the strip
   * ignores this. Also honours a dismissal the scratcher's first build moved
   * into its own store and deleted from here, before the popup came back —
   * otherwise those visitors would be offered it again inside their 30 days.
   */
  function isSilenced(): boolean {
    if (!import.meta.client)
      return true
    const now = Date.now()
    if (newsletterAnswerSilences(readAnswer(), now))
      return true
    const migratedAt = migratedNewsletterDismissalAt(readScratchStore())
    return !!migratedAt && newsletterAnswerSilences({ kind: 'dismissed', at: migratedAt }, now)
  }

  function recordAnswer(kind: NewsletterAnswerKind): void {
    if (!import.meta.client)
      return
    // A sign-up is final: closing the popup after reopening it from the strip
    // must not turn a subscriber back into someone it may ask again in 30 days.
    if (kind === 'dismissed' && readAnswer()?.kind === 'subscribed')
      return
    const answer: NewsletterAnswer = { kind, at: Date.now() }
    inMemoryAnswer = answer
    try {
      window.localStorage.setItem(LEGACY_NEWSLETTER_ANSWER_KEY, JSON.stringify(answer))
    }
    catch {
      // The in-memory mirror above still carries this visit.
    }
  }

  function show(): void {
    mounted.value = true
    open.value = true
  }

  const markDismissed = () => recordAnswer('dismissed')
  const markSubscribed = () => recordAnswer('subscribed')

  return { open, mounted, show, isSilenced, markDismissed, markSubscribed }
}
